import React, { useMemo } from 'react';

/**
 * Satellite tile layer rendered as <image> elements inside the parent SVG.
 *
 * Uses Esri World Imagery (free, no API key required, attribution-only).
 * Tiles are Web-Mercator but we project them through the parent's linear
 * projection so they line up perfectly with the existing GPS trajectory.
 * For a single racing circuit (max ~5 km bounding box) the difference between
 * Web-Mercator and linear lat/lon is < 1 m so seams are imperceptible.
 *
 * Props:
 *   bbox: { minLat, maxLat, minLon, maxLon }  GPS bounding box
 *   project: (lat, lon) => [x, y]             linear SVG projection
 *   width, height: viewport hint used to choose the right zoom level
 *   opacity: 0..1
 *   provider: 'esri' | 'osm-streets'
 */
const PROVIDERS = {
  esri: {
    url: (z, x, y) => `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`,
    maxZoom: 19,
    label: 'Esri World Imagery © Esri, Maxar',
  },
  'osm-streets': {
    url: (z, x, y) => `https://a.tile.openstreetmap.org/${z}/${x}/${y}.png`,
    maxZoom: 19,
    label: '© OpenStreetMap',
  },
};

// --- Web Mercator tile math --------------------------------------------------
const lon2tile = (lon, z) => ((lon + 180) / 360) * Math.pow(2, z);
const lat2tile = (lat, z) => {
  const rad = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * Math.pow(2, z);
};
const tile2lon = (x, z) => (x / Math.pow(2, z)) * 360 - 180;
const tile2lat = (y, z) => {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z);
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
};

export default function SatelliteTiles({ bbox, project, width = 800, height = 600, opacity = 1, provider = 'esri' }) {
  const tiles = useMemo(() => {
    if (!bbox) return null;
    const { minLat, maxLat, minLon, maxLon } = bbox;
    const prov = PROVIDERS[provider] || PROVIDERS.esri;

    // Pick the HIGHEST zoom level whose bbox still fits in ≤8 tiles per
    // dimension. Higher z = better pixel resolution on the satellite image.
    // We cap at 64 tiles total so a circuit never explodes our tile budget.
    let z = 14;
    for (let cand = prov.maxZoom; cand >= 13; cand--) {
      const tx0 = lon2tile(minLon, cand);
      const tx1 = lon2tile(maxLon, cand);
      const ty0 = lat2tile(maxLat, cand);
      const ty1 = lat2tile(minLat, cand);
      const wt = Math.abs(tx1 - tx0);
      const ht = Math.abs(ty1 - ty0);
      if (wt <= 8 && ht <= 8) { z = cand; break; }
    }

    const x0 = Math.floor(lon2tile(minLon, z));
    const x1 = Math.floor(lon2tile(maxLon, z));
    const y0 = Math.floor(lat2tile(maxLat, z));
    const y1 = Math.floor(lat2tile(minLat, z));

    const out = [];
    for (let tx = x0; tx <= x1; tx++) {
      for (let ty = y0; ty <= y1; ty++) {
        const tLat0 = tile2lat(ty, z);
        const tLat1 = tile2lat(ty + 1, z);
        const tLon0 = tile2lon(tx, z);
        const tLon1 = tile2lon(tx + 1, z);
        // Project the two opposite corners of the tile through the parent's
        // linear projection — this is what makes the tile line up with the
        // GPS trajectory regardless of the parent's scale.
        const [px0, py0] = project(tLat0, tLon0);
        const [px1, py1] = project(tLat1, tLon1);
        out.push({
          z, tx, ty,
          x: Math.min(px0, px1),
          y: Math.min(py0, py1),
          w: Math.abs(px1 - px0),
          h: Math.abs(py1 - py0),
          href: prov.url(z, tx, ty),
        });
      }
    }
    return { z, tiles: out };
  }, [bbox, project, provider, width, height]);

  if (!tiles) return null;
  return (
    <g data-testid="satellite-tiles" style={{ opacity }}>
      {tiles.tiles.map((t) => (
        <image
          key={`${t.z}-${t.tx}-${t.ty}`}
          href={t.href}
          x={t.x}
          y={t.y}
          width={t.w}
          height={t.h}
          preserveAspectRatio="none"
          crossOrigin="anonymous"
        />
      ))}
    </g>
  );
}
